import { useEffect } from 'react';

import { Button, Form, message } from 'antd';
import { useDispatch } from 'react-redux';

import * as Actions from 'flex/Actions';
import { useAppSelector } from 'flex/utils';
import { useGetSettings } from '../hooks/useGetSettings';
import { useUpdateSettings } from '../hooks/useUpdateSettings';
import { convertDataFromResponseFormat, convertDataToRequestFormat, getIsEnableMFAFromSetting } from './utils/Helper';
import { IpFilterFormItem } from './IpListField';
import AdminSecuritySection from './AdminSecuritySection';
import MfaSection from './MfaSection';
import { TabKeyEnum } from '..';

/** フォーム全体の値の型 */
export type FormValues = {
    // 多要素認証セクション
    enableMfa: boolean;
    requireMfaPermission: string[];
    mfaSkipSameDevice: boolean;
    mfaSkipDays: number;
    mfaSkipHours: number;
    mfaAllowIpsEnabled: boolean;
    mfaAllowedIps: IpFilterFormItem[];
    // 全体管理者画面セクション
    requireMfaControl: boolean;
    controlIpFiltersEnabled: boolean;
    controlIpFilters: IpFilterFormItem[];
    // 学校管理者画面セクション
    requireMfaAdmin: boolean;
    adminIpFiltersEnabled: boolean;
    adminIpFilters: IpFilterFormItem[];
};

/** 全体管理者画面セクションの設定値 */
const CONTROL_MFA = {
    TITLE: '全体管理者画面のセキュリティ設定',
    TOOLTIP: '2名以上の全体管理者で認証アプリ(Authenticator)を登録することで、この機能を設定できます。',
} as const;

/** 学校管理者画面セクションの設定値 */
const ADMIN_MFA = {
    TITLE: '学校管理画面のセキュリティ設定',
    TOOLTIP: '学校管理者権限と教員権限の両方の多要素認証を設定可能にすることで、この機能を設定できます。',
} as const;

/** IP フィルター配列のデフォルト初期値 (未登録時は空配列とし、UI 側で「＋」ボタンのみ表示する) */
const DEFAULT_IP_FILTERS: IpFilterFormItem[] = [];

type Props = {
    tabActiveKey: (typeof TabKeyEnum)[keyof typeof TabKeyEnum]
    hasTouchScreen: boolean
}

/**
 * セキュリティ設定ページ
 * 多要素認証の有効化・全体管理者・学校管理者 画面のセキュリティを管理する
 */
const SettingTab: React.FC<Props> = (props) => {
    const [form] = Form.useForm<FormValues>();
    const dispatch = useDispatch();
    const isControllableUser = useAppSelector(state => state.isControllableUser.payload);

    // セキュリティ設定全体 更新フック
    const [updateSettingsState, updateSettings] = useUpdateSettings(
        () => {
            message.success('セキュリティ設定を更新しました');
        }
    );

    // セキュリティ設定全体 取得フック
    const [getSettingsState, getSettings] = useGetSettings(
        (_meta, payload) => {
            const data = convertDataFromResponseFormat(payload.result);

            form.setFieldsValue(data);
        }
    );

    useEffect(() => {
        if (props.tabActiveKey !== 'settings') return;
        // 初回マウント時にデフォルト初期値を設定してから API を呼び出す
        form.setFieldsValue({
            controlIpFilters: DEFAULT_IP_FILTERS,
            controlIpFiltersEnabled: false,
            requireMfaControl: false,
            requireMfaPermission: [],
            enableMfa: false,
            mfaAllowedIps: DEFAULT_IP_FILTERS,
            mfaAllowIpsEnabled: false,
            mfaSkipDays: 3,
            mfaSkipHours: 0,
            mfaSkipSameDevice: false,
            adminIpFilters: DEFAULT_IP_FILTERS,
            adminIpFiltersEnabled: false,
            requireMfaAdmin: false,
        });
        getSettings();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.tabActiveKey]);

    /**
     * フォーム送信ハンドラ
     * フォーム値を API リクエスト形式に変換して control/security/setting へ送信する
     */
    const handleFinish = (values: FormValues) => {
        const postData = convertDataToRequestFormat(values);
        if (isControllableUser) {
            const isEnableMFA = getIsEnableMFAFromSetting(values.enableMfa, values.requireMfaPermission);
            dispatch(Actions.data.isEnableMFA(isEnableMFA));
            dispatch(Actions.data.isEnableTenantMFA(values.enableMfa));
        }
        updateSettings(postData);
    };

    const isLoading = getSettingsState.isLoading || updateSettingsState.isLoading;

    /**
     * フォーム値変更ハンドラ (ユーザー操作時のみ呼ばれ、setFieldsValue では呼ばれない)
     * 「認証アプリを登録できるユーザー」で学校管理者権限・教員権限のどちらかが外れたら、
     * 学校管理画面の「多要素認証を要求する」を「要求しない」に戻す (LGT-4757)。
     * 片方のみの状態では該当セクションが disabled になるだけで値は送信されるため、
     * 「要求する」のまま保存できてしまっていた
     */
    const handleValuesChange = (changedValues: Partial<FormValues>) => {
        if (!changedValues.requireMfaPermission) return;
        const permission = changedValues.requireMfaPermission;
        if (!permission.includes('school_administrator') || !permission.includes('teacher')) {
            form.setFieldsValue({ requireMfaAdmin: false });
        }
    };

    // requireMfaPermission の変化を監視し、各セクションの有効/無効を制御する
    const requireMfaPermission: string[] = Form.useWatch('requireMfaPermission', form) ?? [];
    // 2名以上の全体管理者が認証アプリを登録済み (can_set_require_mfa_control が true) の場合のみ、全体管理者画面セクションを有効化する
    const isControlSectionEnabled = getSettingsState?.data?.can_set_require_mfa_control ?? false;

    // 学校管理者権限と教員権限の両方が選択されている場合、学校管理画面セクションを有効化する
    const isAdminSectionEnabled = requireMfaPermission.includes('school_administrator') && requireMfaPermission.includes('teacher');

    return (
        <div className='page-security container'>
            <Form<FormValues>
                form={form}
                onFinish={handleFinish}
                onValuesChange={handleValuesChange}
                {...layout}
            >
                {/* 多要素認証の有効化セクション */}
                <MfaSection
                    disabled={isLoading}
                    form={form}
                    layoutWithAddButton={layoutWithAddButton}
                    layoutWithoutLabel={layoutWithoutLabel}
                />

                {/* 全体管理者画面のセキュリティ設定セクション */}
                <AdminSecuritySection
                    disabled={isLoading || !isControlSectionEnabled}
                    ipFilterDisabled={isLoading}
                    form={form}
                    ipFiltersFieldName='controlIpFilters'
                    isIpFiltersEnableName='controlIpFiltersEnabled'
                    layoutWithAddButton={layoutWithAddButton}
                    layoutWithoutLabel={layoutWithoutLabel}
                    mfaRequiredFieldName='requireMfaControl'
                    title={CONTROL_MFA.TITLE}
                    tooltip={CONTROL_MFA.TOOLTIP}
                />

                {/* 学校管理者画面のセキュリティ設定セクション */}
                <AdminSecuritySection
                    disabled={isLoading || !isAdminSectionEnabled}
                    ipFilterDisabled={isLoading}
                    form={form}
                    ipFiltersFieldName='adminIpFilters'
                    isIpFiltersEnableName='adminIpFiltersEnabled'
                    layoutWithAddButton={layoutWithAddButton}
                    layoutWithoutLabel={layoutWithoutLabel}
                    mfaRequiredFieldName='requireMfaAdmin'
                    title={ADMIN_MFA.TITLE}
                    tooltip={ADMIN_MFA.TOOLTIP}
                />

                <div className='flex-right-container mt-4'>
                    <Button
                        disabled={isLoading}
                        htmlType='submit'
                        loading={isLoading}
                        type='primary'
                    >
                        保存
                    </Button>
                </div>
            </Form>
        </div>
    );
};

export default SettingTab;

// フォームレイアウト設定
const layout = {
    labelCol: {
        sm: { span: 10 },
        xl: { span: 9 },
        xxl: { span: 8 },
    },
    wrapperCol: {
        sm: { span: 14 },
        xl: { span: 15 },
        xxl: { span: 16 },
    },
};

const layoutWithoutLabel = {
    wrapperCol: {
        sm: { offset: layout.labelCol.sm.span },
        xl: { offset: layout.labelCol.xl.span },
        xxl: { offset: layout.labelCol.xxl.span },
    },
};

const layoutWithAddButton = {
    wrapperCol: {
        sm: { offset: layout.labelCol.sm.span },
        xl: { offset: layout.labelCol.xl.span },
        xxl: { offset: layout.labelCol.xxl.span },
    },
};